/**
 * Composant de traçabilité
 * Affiche les métadonnées: sources consultées, timestamp, etc.
 * Optimisé pour l'impression A4 (SOURCE-UX-001)
 */
import { SubstanceFiche } from '../../services/api'
import { INRS_LAST_UPDATE } from '../../config/sources'

interface TraceabilityProps {
  meta: SubstanceFiche['meta']
  sources: SubstanceFiche['sources']
}

interface SourceStatus {
  key: string
  name: string
  disponible: boolean
  date_donnees: string | null
  colorClass: string
}

export default function Traceability({ meta, sources }: TraceabilityProps) {
  const sourceStatuses: SourceStatus[] = [
    {
      key: 'inrs',
      name: 'INRS',
      disponible: sources.inrs.disponible,
      // INRS: date de mise à jour globale de la base (pas de date par substance)
      date_donnees: sources.inrs.disponible ? `Mise à jour base: ${INRS_LAST_UPDATE}` : null,
      colorClass: 'text-inrs',
    },
    {
      key: 'anses',
      name: 'ANSES',
      disponible: sources.anses.disponible,
      date_donnees: sources.anses.date_donnees,
      colorClass: 'text-anses',
    },
    {
      key: 'echa',
      name: 'ECHA',
      disponible: sources.echa.disponible,
      date_donnees: sources.echa.date_donnees,
      colorClass: 'text-echa',
    },
    {
      key: 'agritox',
      name: 'AGRITOX',
      disponible: sources.agritox.disponible,
      date_donnees: sources.agritox.date_donnees,
      colorClass: 'text-agritox',
    },
  ]

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
      <div className="flex items-center gap-2 mb-3">
        <svg
          className="w-4 h-4 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="font-medium text-gray-700">Traçabilité des données</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {sourceStatuses.map((source) => (
          <div
            key={source.key}
            className={`
              flex flex-col p-2 rounded border
              ${source.disponible ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-100'}
            `}
          >
            <div className="flex items-center gap-1.5">
              <span
                className={`w-2 h-2 rounded-full ${
                  source.disponible ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
              <span
                className={`font-medium ${source.disponible ? source.colorClass : 'text-gray-400'}`}
              >
                {source.name}
              </span>
            </div>
            <span className="text-xs text-gray-500 mt-1">
              {source.disponible
                ? source.date_donnees || 'Date non précisée'
                : 'Non disponible'}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-4 text-xs text-gray-500">
        <span>
          <strong>{meta.nombre_sources}</strong> source(s) active(s)
        </span>
        <span className="hidden sm:inline">•</span>
        <span>
          Sources: {meta.sources_trouvees.join(', ') || 'Aucune'}
        </span>
      </div>
    </div>
  )
}
